import { Controller } from "@hotwired/stimulus"
import ProgressBar from "../helpers/progress_bar"

const JQUERY_NAMESPACE = ".subscriptionEvents"

// Paired with the shared `datatable` controller on the same wrapping element: that one owns the
// DataTables lifecycle, the paginate-button styling and the row highlight; this one owns the
// date-range picker and the extra `.dataTables_info` hide/show, which no other page needs.
//
// The `.datatable-moveable-filter` listener and the `#filter_sender` select2 init below are both
// inert: the view's select renders with class "datatable-filter", not "-moveable-", so the
// listener never fires for it, and SubscriptionEventDatatable#get_raw_records never reads
// params[:filter] anyway. Both are long-standing.
export default class extends Controller {
  static get targets() {
    return ["table", "dateRangeInput", "senderFilter"];
  }

  static get values() {
    return { begin: String, end: String };
  }

  connect() {
    this.onDraw = this.onDraw.bind(this);
    $(this.tableTarget).on(`draw.dt${JQUERY_NAMESPACE}`, this.onDraw);

    $(this.dateRangeInputTarget).daterangepicker({
      buttonClasses: "btn btn-main btn-md",
      cancelButtonClasses: "btn-ghost",
      opens: "left",
      alwaysShowCalendars: true,
      showCustomRangeLabel: false,
      ranges: {
        "Today": [moment(), moment()],
        "Yesterday": [moment().subtract(1, "days"), moment().subtract(1, "days")],
        "Last 7 Days": [moment().subtract(6, "days"), moment()],
        "Last 30 Days": [moment().subtract(29, "days"), moment()],
        "This Month": [moment().startOf("month"), moment().endOf("month")],
        "Last Month": [moment().subtract(1, "month").startOf("month"), moment().subtract(1, "month").endOf("month")],
        "All Time": [moment(this.beginValue, "MM-DD-YYYY"), moment(this.endValue, "MM-DD-YYYY")],
      },
    }, (start, end) => this.applyRange(start, end));

    $(this.element).on(`change${JQUERY_NAMESPACE}`, ".datatable-moveable-filter", () => this.reload());

    $(this.senderFilterTarget).select2({ placeholder: "Created By", width: "100%" });
  }

  disconnect() {
    $(this.tableTarget).off(JQUERY_NAMESPACE);

    const picker = $(this.dateRangeInputTarget).data("daterangepicker");
    if (picker) { picker.remove(); }
    $(this.dateRangeInputTarget).off(JQUERY_NAMESPACE);

    $(this.element).off(JQUERY_NAMESPACE);

    if ($(this.senderFilterTarget).data("select2")) {
      $(this.senderFilterTarget).select2("destroy");
    }
  }

  applyRange(start, end) {
    const newRange = `${start.format("MMDDYYYY")}-${end.format("MMDDYYYY")}`;
    this.reload({ range: newRange });
  }

  // The shared `datatable` controller already hides/shows `.dataTables_paginate` under the same
  // condition; `.dataTables_info` is this page's own addition.
  onDraw() {
    const api = $(this.tableTarget).DataTable();
    const settings = api.settings()[0];
    const info = settings.nTableWrapper.querySelector(".dataTables_info");
    if (!info) { return; }

    // No stylesheet rule sets a competing `display: none` on .dataTables_info (checked
    // app/assets/stylesheets), so clearing the inline style is a faithful native equivalent of
    // jQuery 3's .show() here -- see the jquery3-show-hazard lore entry for when that would not
    // be safe.
    info.style.display = settings._iDisplayLength > settings.fnRecordsDisplay() ? "none" : "";
  }

  // The ProgressBar calls matter: its bar really does enter the DOM during this reload.
  reload(extraParams = {}) {
    ProgressBar.start();

    const filterSelects = this.element.querySelectorAll(".datatable-moveable-filter, .datatable-filter");
    if (filterSelects.length > 0) {
      const filter = {};
      filterSelects.forEach((el) => {
        const key = el.getAttribute("name").replace(/(^.*\[|\].*$)/g, "");
        // select2's own data API, not a plain DOM read -- has to stay jQuery-wrapped.
        filter[key] = ($(el).select2("data") || []).map((option) => option.id);
      });
      extraParams["filter"] = filter;
    }

    this.element.querySelectorAll(".datatable-moveable-text-filter, .datatable-text-filter").forEach((el) => {
      extraParams["filter"][el.getAttribute("name").replace(/(^.*\[|\].*$)/g, "")] = el.value;
    });

    const params = Object.assign(this.currentParams(), extraParams);

    const replacement = `${this.tableTarget.getAttribute("data-source").split("?")[0]}?${$.param(params)}`;
    this.tableTarget.setAttribute("data-source", replacement);
    $(this.tableTarget).DataTable().ajax.url(replacement).load(() => ProgressBar.stop());
  }

  currentParams() {
    return this.urlParamsAsObject(this.tableTarget.getAttribute("data-source"));
  }

  // Converts a bracket-notation query string into the nested object `$.param()` re-serializes
  // back into the same form. A URLSearchParams rewrite would lose the nesting.
  urlParamsAsObject(dataSource) {
    const query = dataSource.substring(dataSource.indexOf("?") + 1);
    const regex = /([^&=]+)=?([^&]*)/g;
    const decode = (str) => decodeURIComponent(str.replace(/\+/g, " "));

    const params = {};
    let match = regex.exec(query);
    while (match) {
      const k = decode(match[1]);
      const v = decode(match[2]);

      if (k.substring(k.length - 2) === "[]") {
        const key = k.substring(0, k.length - 2);
        (params[key] || (params[key] = [])).push(v);
      } else {
        params[k] = v;
      }

      match = regex.exec(query);
    }

    const assignPath = (obj, keyPath, value) => {
      const lastKeyIndex = keyPath.length - 1;
      let target = obj;
      for (let i = 0; i < lastKeyIndex; i++) {
        const key = keyPath[i];
        if (!(key in target)) { target[key] = {}; }
        target = target[key];
      }
      target[keyPath[lastKeyIndex]] = value;
    };

    Object.keys(params).forEach((prop) => {
      const structure = prop.split("[");
      if (structure.length > 1) {
        const levels = structure.map((part) => part.replace(/[?[\]\\ ]/g, ""));
        assignPath(params, levels, params[prop]);
        delete params[prop];
      }
    });

    return params;
  }
}
